import {
  ID_SEARCH_PATTERN_TEXT_PREFIX,
  SPECIFIC_SEARCH_PARSABLE_EXPRESSION,
  SPECIFIC_SEARCH_PATTERN,
} from "../constants";
import type { IExtendableDto } from "../dto";
import { StarredDisplayModeEnum } from "../enums";
import type { ITypeormWhereCriteria, RydeUiUnit } from "../models";
import { isEntityServerSidePaginated } from "./common.helpers";

export const parseSearchString = (
  searchText: string | undefined,
): ITypeormWhereCriteria<IExtendableDto> => {
  if (!searchText?.trim()?.length) {
    return {};
  }

  if (!searchText.includes(",")) {
    if (+searchText.trim() > 0) {
      return { id: { $in: [+searchText.trim()] } };
    } else {
      return {};
    }
  }

  const codesArr: number[] = [];
  searchText.split(",").forEach(x => {
    if (+x?.trim() > 0) {
      codesArr.push(+x?.trim());
    }
  });

  if (!codesArr.length) {
    return {};
  }

  return { id: { $in: [...codesArr] } };
};

export const getSearchByIdWhereObject = ({
  ids,
  returnNoResultsOnEmptyArray,
}: {
  ids: number[];
  returnNoResultsOnEmptyArray?: boolean;
}): ITypeormWhereCriteria<IExtendableDto> => {
  if (!ids.length) {
    return { id: { $in: returnNoResultsOnEmptyArray ? [-1] : [] } };
  }

  return { id: { $in: ids } };
};

export const getSearchExcludeByIdWhereObject = (
  ids: readonly number[],
): ITypeormWhereCriteria<IExtendableDto> => {
  if (!ids.length) {
    return {};
  }

  const excludeByIdWhere = {
    id: { $not: { $in: [...ids] } },
  } satisfies ITypeormWhereCriteria<IExtendableDto>;

  return excludeByIdWhere;
};

export const getStarredDisplayModeWhere = ({
  mode,
  starredIds,
}: {
  mode: StarredDisplayModeEnum;
  starredIds: readonly number[];
}): ITypeormWhereCriteria<IExtendableDto> => {
  if (
    mode === StarredDisplayModeEnum.FiltersApplied ||
    mode === StarredDisplayModeEnum.FiltersIgnored
  ) {
    return getSearchByIdWhereObject({
      ids: [...starredIds],
      returnNoResultsOnEmptyArray: true,
    });
  }

  if (mode === StarredDisplayModeEnum.ShowAllWithoutStarred) {
    return getSearchExcludeByIdWhereObject(starredIds);
  }

  return {};
};

export const shouldRefetchGridOnStarredDisplayModeChange = ({
  entity,
  previousDisplayMode,
  nextDisplayMode,
}: {
  entity: RydeUiUnit;
  previousDisplayMode: StarredDisplayModeEnum;
  nextDisplayMode: StarredDisplayModeEnum;
}): boolean => {
  if (previousDisplayMode === nextDisplayMode) {
    return false;
  }

  if (isEntityServerSidePaginated(entity)) {
    return true;
  }

  return (
    nextDisplayMode === StarredDisplayModeEnum.FiltersIgnored ||
    previousDisplayMode === StarredDisplayModeEnum.FiltersIgnored
  );
};

export const getSpecificSearchPatterMatch = (
  searchValue: string,
): RegExpMatchArray | null => {
  return searchValue.match(SPECIFIC_SEARCH_PATTERN);
};

export const parseSpecificSearchStringToArray = (
  searchText: string,
): number[] => {
  const match = getSpecificSearchPatterMatch(searchText);

  if (!match) {
    return [];
  }

  const ids = match[1];

  if (!SPECIFIC_SEARCH_PARSABLE_EXPRESSION.test(ids)) {
    return [];
  }

  return ids
    .split(",")
    .map(s => Number(s.trim()))
    .filter(n => Number.isFinite(n));
};

export const generateSpecificSearchPattern = (ids: number[]): string => {
  return `${ID_SEARCH_PATTERN_TEXT_PREFIX} ${ids.join(",")}`;
};

export const debugModeToApiParam = (
  debugMode?: boolean,
): number | undefined => {
  return debugMode ? 1 : undefined;
};

export const parseGlobalSearchRideIdsStringToArray = (
  searchText: string,
): number[] => {
  return searchText
    .split(",")
    .map(s => Number(s.trim()))
    .filter(n => isNaN(n) === false);
};
